import { Skeleton } from '@dashboard/ui'
import { SendStatus, WebhookRedacted } from '@/api/dashboard'
import { TableCell, TableRow } from '@/ui'
import { GAME_WEBHOOKS_DETAIL_PATH, useGenerateCompanyPath } from '@/libs'
import { WebhookStatusBadge } from './WebhookStatusBadge'
import { useQuery } from '@tanstack/react-query'
import { webhooksTotalStatQuery } from '@/layouts/webhooks/api/webhooksStatQuery'
import AnimatedDots from '@/components/animated-dots/AnimatedDots'
import { WebhookStatChart } from '@/layouts/webhooks/widgets/WebhookStatChart'

interface WebhookRowProps {
  webhook: WebhookRedacted
  canEdit: boolean
}

// Minimum threshold for displaying failure rate with decimals
// Values below this threshold will be displayed as "0%"
const MIN_DISPLAY_THRESHOLD = 0.01

const formatFailureRate = (failurePercentage: number): string => {
  // If the value is less than the minimum threshold, display as "0%"
  if (failurePercentage < MIN_DISPLAY_THRESHOLD) {
    return '0%'
  }
  // Otherwise, display with two decimal places
  return `${failurePercentage.toFixed(2)}%`
}

export const WebhookRow = ({ webhook, canEdit }: WebhookRowProps) => {
  const { companyId, gameId, generatePath } = useGenerateCompanyPath()
  const { data: totalData, isLoading } = useQuery(webhooksTotalStatQuery(companyId, gameId, webhook.id))

  const renderErrorRate = () => {
    if (isLoading || !totalData) {
      return <AnimatedDots />
    }

    const success = totalData[SendStatus.Success]?.value
    const fail = totalData[SendStatus.Error]?.value

    if (!success && !fail) {
      return 'N/A'
    }

    const failurePercentage = (fail / (success + fail)) * 100
    return <span>{formatFailureRate(failurePercentage)}</span>
  }

  return (
    <TableRow to={canEdit ? generatePath(GAME_WEBHOOKS_DETAIL_PATH, { webhookId: webhook.id }) : undefined}>
      <TableCell>
        <div className="truncate text-caption-md text-text-brand-primary">{webhook.url}</div>
        <div className="mt-1 line-clamp-1 text-caption-md font-normal text-text-tertiary">
          {webhook.description || '–'}
        </div>
      </TableCell>
      <TableCell className="w-[300px]">
        <WebhookStatChart webhookId={webhook.id} simpleMode={true} height="32px" />
      </TableCell>
      <TableCell className="max-w-36">{renderErrorRate()}</TableCell>
      <TableCell className="max-w-36">
        <WebhookStatusBadge webhook={webhook} />
      </TableCell>
    </TableRow>
  )
}

export const WebhookRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Skeleton className="h-[18px] w-2/3" />
      <Skeleton className="mt-1 h-[18px] w-1/4" />
    </TableCell>
    <TableCell className="max-w-36">
      <Skeleton className="h-[18px] w-1/2" />
    </TableCell>
    <TableCell className="max-w-36">
      <Skeleton className="h-[18px] w-1/2" />
    </TableCell>
    <TableCell className="flex max-w-36 justify-end">
      <Skeleton className="h-6 w-1/2" />
    </TableCell>
  </TableRow>
)
